

import { CalendarProgress, CourseProgress, CourseLesson } from '@shared/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://app.netasfactory.co.il/api';

// Frontend User type with string dates (as received from API)
interface FrontendUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  isActive: boolean;
  purchaseDate: string;
  lastLoginDate?: string;
  cyclesCompleted: number;
}

interface LoginResponse {
  success: boolean;
  user: FrontendUser;
  calendarProgress?: CalendarProgress[];
  courseProgress?: CourseProgress[];
  courseLessons?: CourseLesson[];
  token: string;
  message: string;
  messageHe: string;
  version?: string;
  timestamp: string;
}

interface SessionResponse {
  success: boolean;
  data: {
    user: FrontendUser;
    calendarProgress?: CalendarProgress[];
    courseProgress?: CourseProgress[];
    courseLessons?: CourseLesson[];
    isAuthenticated: boolean;
    token?: string;
  };
  timestamp: string;
}

interface ApiError {
  success: false;
  error: {
    code: string;
    message: string;
    messageHe: string;
    details?: any;
  };
  timestamp: string;
}

/**
 * Login with email and password
 */
export const login = async (email: string, password: string): Promise<LoginResponse> => {
  const response = await fetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Important for cookies
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.error?.messageHe || error.error?.message || 'התחברות נכשלה');
  }

  return response.json();
};

/**
 * Validate current session
 */
export const validateSession = async (): Promise<SessionResponse | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/validate`, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      return null;
    }

    return response.json();
  } catch (error) {
    console.error('Session validation error:', error);
    return null;
  }
};

/**
 * Logout current user
 */
export const logout = async (): Promise<void> => {
  try {
    await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch (error) {
    console.error('Logout error:', error);
    // Don't throw - logout should always succeed locally
  }
};

/**
 * Create password for new user
 */
export const createPassword = async (
  userId: string,
  password: string
): Promise<LoginResponse> => {
  const response = await fetch(`${API_BASE_URL}/auth/create-password/${userId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ password }),
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.error?.messageHe || error.error?.message || 'יצירת סיסמה נכשלה');
  }

  return response.json();
};

/**
 * Request password reset
 */
export const requestPasswordReset = async (email: string): Promise<{ 
  success: boolean; 
  messageHe: string;
}> => {
  const response = await fetch(`${API_BASE_URL}/auth/password-reset/request`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  });

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error?.messageHe || data.error?.message || 'בקשת איפוס סיסמה נכשלה');
  }

  return data;
};

/**
 * Reset password with token
 */
export const resetPassword = async (
  token: string,
  newPassword: string
): Promise<{ success: boolean; messageHe: string }> => {
  const response = await fetch(`${API_BASE_URL}/auth/password-reset/reset`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token, newPassword }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error?.messageHe || data.error?.message || 'איפוס סיסמה נכשל');
  }

  return data;
};

export const authApi = {
  login,
  validateSession,
  logout,
  createPassword,
  requestPasswordReset,
  resetPassword,
};

export default authApi;
